// The scores strip for templates that show one on every page (Arena's
// ticker; §15.2, ARCHITECTURE §16 "ticker on every page").
//
// Source: /public/homepage (upcoming + recent results + featured standings
// for records), the same payload Home uses, so there is no new endpoint.
// Opt-in and lazy: classic never calls it, so its request order is
// untouched. One request per sport, shared by every caller and kept for
// 30 s across navigations; while any game is live it refreshes every 30 s.
//
// Wording is not decided here: rows carry the raw status and core's
// formatted event time (EventWhen); templates turn those into words with
// useLabels() (status(), ui.*).
//
// #583: with 2+ live divisions the strip follows the division pick
// (`/public/homepage?division=<id>|all`, cached per sport AND pick) and each
// game carries its division tag. Below that it is exactly today's request.

import { useEffect, useMemo, useState } from 'react'
import { api } from '@/services/api'
import type { HomepageData } from '@/services/api/types'
import { useSportContext } from '@/contexts/SportContext'
import type { DivisionArg, DivisionRef } from '@/services/api/types'
import { divisionArg, divisionRef, useDivision } from '../division'
import { deriveRecords, eventWhen, teamRef } from '../pages/home/derive'
import type { EventWhen, TeamRef } from '../pages/home/types'
import { dayKey } from '../../kit/time/relativeDay'
import { useSiteContentAllowed } from '../access/context'

export const STRIP_TTL_MS = 30_000
export const STRIP_LIVE_POLL_MS = 30_000
/** Upcoming window, in local days after today. */
export const STRIP_DAYS_AHEAD = 5

export interface StripTeam extends TeamRef {
  score: number | null
  /** '3-0' (W-L) or '2-0-1' (W-D-L) from the active season's standings, else null. */
  record: string | null
  winner: boolean
}

export type StripGameStatus = 'scheduled' | 'live' | 'final' | 'postponed' | 'cancelled'

export interface StripGame {
  id: number
  href: string
  status: StripGameStatus
  /** The API status, verbatim ('in_progress', 'completed', …). */
  rawStatus: string
  when: EventWhen
  home: StripTeam
  away: StripTeam
  competitionName: string | null
  /** #583: the game's division tag (null = none). Present only with 2+ live divisions. */
  division?: DivisionRef | null
}

export interface StripDay {
  /** YYYY-MM-DD in the game's zone. */
  key: string
  /** EventWhen.date of the day's first game ('Oct 6'). */
  date: string
  today: boolean
  games: StripGame[]
}

export interface ScoreboardStrip {
  status: 'idle' | 'loading' | 'ready' | 'empty' | 'error'
  days: StripDay[]
  liveCount: number
  scheduleHref: '/schedule'
}

type Entry = { at: number; promise: Promise<HomepageData>; data?: HomepageData }
const cache = new Map<string, Entry>()

/** The cache key: the sport, plus the division pick only when there is one. */
const stripKey = (sport: string, division: DivisionArg | undefined) => (division === undefined ? sport : `${sport}:d${division}`)

function fetchStrip(sport: string, force = false, division?: DivisionArg): Entry {
  const key = stripKey(sport, division)
  const hit = cache.get(key)
  if (hit && !force && Date.now() - hit.at < STRIP_TTL_MS) return hit
  const entry: Entry = {
    at: Date.now(),
    // Explicit sport: never rely on the global stamp, so the cache key and the
    // request can't disagree ('default' = no sport known yet → unscoped).
    promise: api.getHomepage({ sport: sport === 'default' ? null : sport, division }).then(
      (d) => { entry.data = d; return d },
      (e) => { if (cache.get(key) === entry) cache.delete(key); throw e },
    ),
  }
  cache.set(key, entry)
  return entry
}

/** Test hook. */
export function resetScoreboardStripCache(): void {
  cache.clear()
}

function stripStatus(raw: string): StripGameStatus {
  if (raw === 'in_progress') return 'live'
  if (raw === 'completed' || raw === 'final') return 'final'
  if (raw === 'postponed') return 'postponed'
  if (raw === 'cancelled' || raw === 'canceled') return 'cancelled'
  return 'scheduled'
}

const fallbackZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

/** Pure: build the strip from a homepage payload (exported for tests and fixtures). */
export function deriveScoreboardStrip(
  data: HomepageData,
  now: Date = new Date(),
  tag?: (divisionId: number | null | undefined) => DivisionRef | null,
): Omit<ScoreboardStrip, 'status'> {
  const records = deriveRecords(data.featured ?? [], data.activeSeason?.id)
  const color = new Map((data.teams ?? []).map((t) => [t.id, t.primary_color ?? null]))
  const side = (t: Parameters<typeof teamRef>[0] & { score?: number | null }, winner: boolean): StripTeam => {
    const ref = teamRef(t)
    return { ...ref, color: ref.color ?? color.get(t.id) ?? null, score: t.score ?? null, record: records[t.id]?.text ?? null, winner }
  }
  const games: StripGame[] = []
  for (const r of data.recentResults ?? []) {
    const tz = r.timezone || fallbackZone()
    if (!r.date) continue
    // Finals from yesterday and today only (local to the game).
    const diff = (Date.parse(dayKey(now, tz)) - Date.parse(dayKey(r.date, tz))) / 86_400_000
    if (diff < 0 || diff > 1) continue
    const hs = r.homeTeam.score, as = r.awayTeam.score
    games.push({
      id: r.id, href: `/game/${r.id}`, status: 'final', rawStatus: 'completed', when: eventWhen(r.date, r.timezone),
      home: side(r.homeTeam, hs > as), away: side(r.awayTeam, as > hs), competitionName: r.tournamentName,
      ...(tag ? { division: tag(r.division_id) } : {}),
    })
  }
  for (const g of data.upcomingGames ?? []) {
    const status = stripStatus(g.status)
    const tz = g.timezone || fallbackZone()
    if (status !== 'live' && g.date) {
      const diff = (Date.parse(dayKey(g.date, tz)) - Date.parse(dayKey(now, tz))) / 86_400_000
      if (diff > STRIP_DAYS_AHEAD) continue
    }
    games.push({
      id: g.id, href: `/game/${g.id}`, status, rawStatus: g.status, when: eventWhen(g.date, g.timezone),
      home: side(g.homeTeam, false), away: side(g.awayTeam, false), competitionName: g.tournamentName,
      ...(tag ? { division: tag(g.division_id) } : {}),
    })
  }
  // Chronological, live games stay in their day; days in order.
  games.sort((a, b) => (Date.parse(a.when.startsAt ?? '') || 0) - (Date.parse(b.when.startsAt ?? '') || 0))
  const days: StripDay[] = []
  for (const g of games) {
    const tz = g.when.timezone || fallbackZone()
    const key = g.when.startsAt ? dayKey(g.when.startsAt, tz) : 'tbd'
    let day = days.find((d) => d.key === key)
    if (!day) {
      day = { key, date: g.when.date, today: key === dayKey(now, tz), games: [] }
      days.push(day)
    }
    day.games.push(g)
  }
  return { days, liveCount: games.filter((g) => g.status === 'live').length, scheduleHref: '/schedule' }
}

export function useScoreboardStrip(wanted = true): ScoreboardStrip {
  // Registration step 33: nothing until a members-only league's visitor is
  // admitted (always true on a public league).
  const allowed = useSiteContentAllowed()
  const enabled = wanted && allowed
  const { activeSportId } = useSportContext()
  // #723: on detail routes too the strip follows the fan's stored pick.
  const division = useDivision({ storedOnDetail: true })
  const divArg = divisionArg(division)
  // `key` is the sport alone without divisions, so the state below is today's.
  const sport = activeSportId || 'default'
  const key = stripKey(sport, divArg)
  const [state, setState] = useState<{ sport: string; data: HomepageData | null; error: boolean; tick: number }>({ sport: key, data: null, error: false, tick: 0 })

  useEffect(() => {
    if (!enabled) return
    let live = true
    const entry = fetchStrip(sport, state.tick > 0, divArg)
    if (entry.data) setState((s) => ({ ...s, sport: key, data: entry.data!, error: false }))
    else entry.promise.then(
      (data) => { if (live) setState((s) => ({ ...s, sport: key, data, error: false })) },
      () => { if (live) setState((s) => ({ ...s, sport: key, data: null, error: true })) },
    )
    return () => { live = false }
    // `key` is derived from sport and divArg.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sport, divArg, state.tick])

  const liveDivs = division.live
  const tag = useMemo(
    () => (division.enabled ? (id: number | null | undefined) => divisionRef(liveDivs, id) : undefined),
    [division.enabled, liveDivs],
  )
  const derived = useMemo(() => (state.data && state.sport === key ? deriveScoreboardStrip(state.data, undefined, tag) : null), [state.data, state.sport, key, tag])

  // Live refresh.
  const liveCount = derived?.liveCount ?? 0
  useEffect(() => {
    if (!enabled || liveCount === 0) return
    const t = window.setInterval(() => setState((s) => ({ ...s, tick: s.tick + 1 })), STRIP_LIVE_POLL_MS)
    return () => window.clearInterval(t)
  }, [enabled, liveCount])

  if (!enabled) return { status: 'idle', days: [], liveCount: 0, scheduleHref: '/schedule' }
  if (state.error && state.sport === key) return { status: 'error', days: [], liveCount: 0, scheduleHref: '/schedule' }
  if (!derived) return { status: 'loading', days: [], liveCount: 0, scheduleHref: '/schedule' }
  return { status: derived.days.length ? 'ready' : 'empty', ...derived }
}
